import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, Coins } from "lucide-react";
import { creditsService, type CreditsStatus } from "@/services/creditsService";

export function LowBalanceAlert() {
  const { toast } = useToast();
  const lastAlertRef = useRef<{ low: boolean; depleted: boolean }>({
    low: false,
    depleted: false
  });

  const { data } = useQuery<CreditsStatus>({
    queryKey: ["credits-status"],
    queryFn: () => creditsService.getCreditsStatus(),
    refetchInterval: 60000,
    staleTime: 30000
  });

  useEffect(() => {
    if (!data || data.unlimitedAiCredits) return;

    if (data.isDepleted && !lastAlertRef.current.depleted) {
      lastAlertRef.current.depleted = true;
      lastAlertRef.current.low = true;
      
      toast({
        variant: "destructive",
        title: "Credits Depleted",
        description: "Your credits have run out. Please purchase more credits to continue using AI features.",
        duration: 10000,
      });
    } else if (data.isLowBalance && !data.isDepleted && !lastAlertRef.current.low) {
      lastAlertRef.current.low = true;
      
      toast({
        title: "Low Credit Balance",
        description: `Only ${data.balance} credits remaining. Consider purchasing more credits.`,
        duration: 8000,
      });
    }

    if (!data.isDepleted && lastAlertRef.current.depleted) {
      lastAlertRef.current.depleted = false;
    }
    if (!data.isLowBalance && lastAlertRef.current.low) {
      lastAlertRef.current.low = false;
    }
  }, [data, toast]);

  return null;
}

export function BalanceIndicator({ className = "" }: { className?: string }) {
  const { data, isLoading } = useQuery<CreditsStatus>({
    queryKey: ["credits-status"],
    queryFn: () => creditsService.getCreditsStatus(),
    refetchInterval: 60000,
    staleTime: 30000
  });

  if (isLoading || !data) {
    return null;
  }

  const getStatusColor = () => {
    if (data.unlimitedAiCredits) return "text-primary";
    if (data.isDepleted) return "text-destructive";
    if (data.isLowBalance) return "text-yellow-600";
    return "text-muted-foreground";
  };

  const getIcon = () => {
    if (data.unlimitedAiCredits) {
      return <Coins className="h-3 w-3" />;
    }
    if (data.isDepleted || data.isLowBalance) {
      return <AlertTriangle className="h-3 w-3" />;
    }
    return <Coins className="h-3 w-3" />;
  };

  return (
    <div 
      className={`flex items-center gap-1.5 text-xs ${getStatusColor()} ${className}`}
      data-testid="indicator-balance"
    >
      {getIcon()}
      <span className="font-medium">
        {data.unlimitedAiCredits ? "Unlimited" : data.balance.toLocaleString()}
      </span>
      {!data.unlimitedAiCredits && <span className="hidden sm:inline">credits</span>}
    </div>
  );
}
